/**
 * Microsoft 365 Admin Console Inspired Theme
 * Security Personnel Management ERP
 *
 * Based on M365 admin center design language:
 * - Primary Color: Teal (#008272)
 * - Clean, professional appearance
 * - Government-grade enterprise look
 */

import {
  createLightTheme,
  createDarkTheme,
  Theme,
  BrandVariants,
} from '@fluentui/react-components';

/**
 * Security ERP Brand Colors (M365 Teal/Cyan)
 */
const securityBrandRamp: BrandVariants = {
  10: '#001e1a',
  20: '#003329',
  30: '#00483b',
  40: '#005e4d',
  50: '#007560',
  60: '#008d73',
  70: '#00a587',
  80: '#00be9c',
  90: '#00d8b1',
  100: '#00f2c6',
  110: '#33f5d1',
  120: '#66f8dc',
  130: '#99fbe7',
  140: '#ccfdf3',
  150: '#e5fef8',
  160: '#f2fefb',
};

/**
 * Light Theme (Default)
 * Matches M365 admin console appearance
 */
export const createSecurityTheme = (): Theme => {
  return createLightTheme(securityBrandRamp);
};

/**
 * Dark Theme
 * For dark mode support
 */
export const createSecurityDarkTheme = (): Theme => {
  return createDarkTheme(securityBrandRamp);
};

/**
 * Theme tokens for custom styling
 * Use these values for consistent theming throughout the app
 */
export const themeTokens = {
  colors: {
    // Primary brand colors (Teal)
    primary: '#008272',
    primaryHover: '#00695c',
    primaryPressed: '#004d40',
    primaryLight: '#00b3a0',

    // Neutral colors
    white: '#ffffff',
    canvas: '#fafafa',
    canvasAlt: '#f5f5f5',

    // Text colors
    textPrimary: '#323130',
    textSecondary: '#605e5c',
    textTertiary: '#8a8886',

    // Border colors
    border: '#edebe9',
    borderHover: '#e1dfdd',

    // Semantic colors
    success: '#107c10',
    warning: '#ffaa44',
    error: '#d13438',
    info: '#0078d4',
  },

  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },

  typography: {
    // Embedded Segoe UI font (loaded via @font-face in index.css)
    // Ensures consistent M365 appearance across all platforms
    fontFamily:
      "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
    fontSize: {
      xs: '10px',
      sm: '12px',
      base: '14px',
      md: '16px',
      lg: '18px',
      xl: '20px',
      xxl: '24px',
      xxxl: '28px',
    },
    fontWeight: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  borderRadius: {
    sm: '2px',
    md: '4px',
    lg: '8px',
  },

  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 2px 4px rgba(0, 0, 0, 0.1)',
    lg: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
};

/**
 * Layout constants (M365 style)
 */
export const layoutTokens = {
  headerHeight: '48px',
  sidebarWidth: '220px',
  sidebarCollapsedWidth: '48px',
  contentMaxWidth: '1440px',
  cardPadding: '20px',
  cardGap: '16px',
};
